import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import injectSheet from 'react-jss';
import { SelectOption, ButtonBar, Button, Theme as theme } from '@spoiler-alert/ui-library';
import { useQuery } from '@apollo/client';
import { CUSTOMERS_AND_EXPORT_PROFILES } from './export-queries';
import MemorySelect from '../../components/forms/memory-select';
import EXPORT_TYPE_OPTIONS from './enums/export-types';

const styles = {
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  siteSelectWrap: {
    borderRadius: '4px',
    border: `1px ${theme.grey30} solid`,
    marginRight: 8,
    width: 260,
    paddingRight: 8,
    boxSizing: 'content-box',
  },
  siteSelectOption: {
    paddingRight: '10px !important',
  },
  siteListItem: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    marginRight: 25,
  },
};

const ExportManagerNavigation = ({
  classes,
  site,
  setSite,
  setExportProfile,
  exportType,
  setExportType,
  setSheetWriters,
}) => {
  const { data, error } = useQuery(CUSTOMERS_AND_EXPORT_PROFILES);
  const sites = data?.exportManagerQuery?.allSellerOrgs;
  const [siteSelection, setSiteSelection] = useState(null);
  const currentSite = sites && sites.find((s) => s._id === site._id);
  const configurableNegotiationsEnabled = currentSite?.configurableNegotiationsEnabled;
  const awardingOpportunitiesExportEnabled = currentSite?.awardingOpportunitiesExportEnabled;
  const sheetWriters = data?.exportManagerQuery?.possibleSheetWriters;

  const changeSite = (option) => {
    setSiteSelection(option);
    const selectedSite = sites.find((s) => s._id === option[0].value);
    setSite({ _id: selectedSite._id, siteName: selectedSite.siteName });
  };

  useEffect(() => {
    if (currentSite?.exportProfiles?.length > 0) {
      const profile = currentSite.exportProfiles.find((ep) => ep.exportType === exportType);
      if (profile) {
        setExportProfile(profile);
      } else {
        setExportProfile(null);
      }
    } else {
      setExportProfile(null);
    }
  }, [currentSite?._id, exportType]);

  useEffect(() => {
    if (sheetWriters) setSheetWriters(sheetWriters);
  });

  const changeExportProfile = (et) => {
    setExportType(et);
    const profile = currentSite?.exportProfiles?.find((ep) => ep.exportType === et);
    if (profile) {
      setExportProfile(profile);
    } else {
      setExportProfile(null);
    }
  };

  return (
    <div>
      <div className={classes.controls}>
        <div className={classes.siteSelectWrap}>
          <MemorySelect
            cacheId="ManagerSiteSelect"
            minimal
            search
            placeholderText="Select a Site"
            onChange={changeSite}
            selectedItems={siteSelection}
          >
            {sites &&
              sites.map((s) => (
                <SelectOption key={s._id} value={s._id} searchText={s.siteName} className={classes.siteSelectOption}>
                  <div className={classes.siteListItem}>{s.siteName}</div>
                </SelectOption>
              ))}
          </MemorySelect>
        </div>
        <div className={classes.buttonRow}>
          {currentSite && (
            <ButtonBar>
              <Button
                onClick={() => changeExportProfile(EXPORT_TYPE_OPTIONS.OFFER)}
                toggle
                active={exportType === EXPORT_TYPE_OPTIONS.OFFER}
                testId="offer-sheet"
                key="offer"
              >
                Offer
              </Button>
              <Button
                onClick={() => changeExportProfile(EXPORT_TYPE_OPTIONS.AWARD)}
                toggle
                active={exportType === EXPORT_TYPE_OPTIONS.AWARD}
                testId="award-sheet"
                key="award"
              >
                Award
              </Button>
              {(configurableNegotiationsEnabled && (
                <Button
                  onClick={() => changeExportProfile(EXPORT_TYPE_OPTIONS.NEGOTIATION)}
                  toggle
                  active={exportType === EXPORT_TYPE_OPTIONS.NEGOTIATION}
                  testId="negotiation-sheet"
                  key="negotiation"
                >
                  Negotiation
                </Button>
              )) || <></>}
              {(currentSite?.siteName?.toLowerCase() === 'spoiler alert marketplace' && (
                <Button
                  onClick={() => changeExportProfile(EXPORT_TYPE_OPTIONS.MARKETPLACE_AUCTION)}
                  toggle
                  active={exportType === EXPORT_TYPE_OPTIONS.MARKETPLACE_AUCTION}
                  key="marketplace-auction"
                >
                  Marketplace Auction Sheet
                </Button>
              )) || <></>}
              {(awardingOpportunitiesExportEnabled && (
                <Button
                  onClick={() => changeExportProfile(EXPORT_TYPE_OPTIONS.AWARDING_OPPORTUNITIES)}
                  toggle
                  active={exportType === EXPORT_TYPE_OPTIONS.AWARDING_OPPORTUNITIES}
                  testId="awarding-opportunities-sheet"
                  key="awarding-opportunities"
                >
                  Awarding Opportunities
                </Button>
              )) || <></>}
              <Button
                onClick={() => changeExportProfile(EXPORT_TYPE_OPTIONS.SALES_HISTORY)}
                toggle
                active={exportType === EXPORT_TYPE_OPTIONS.SALES_HISTORY}
                testId="sales-history-sheet"
                key="sales-history"
              >
                Sales History
              </Button>
            </ButtonBar>
          )}
        </div>
      </div>
      {error && error.message}
    </div>
  );
};

ExportManagerNavigation.propTypes = {
  classes: PropTypes.object,
  site: PropTypes.object,
  setSite: PropTypes.func,
  setExportProfile: PropTypes.func,
  exportType: PropTypes.string,
  setExportType: PropTypes.func,
  setSheetWriters: PropTypes.func,
};

export default injectSheet(styles)(ExportManagerNavigation);
